import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BLOG_POSTS } from "../lib/blogPosts";
import Seo from "../components/Seo";
import { absoluteUrl } from "../lib/siteConfig";

function BlogCard({ post, featured, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.45, delay: index * 0.06, ease: "easeOut" }}
      className={`glass-card rounded-xl overflow-hidden flex flex-col group ${
        featured ? "lg:col-span-2 lg:row-span-2 module-accent-top" : "border-t-[2px] border-t-transparent hover:border-t-primary transition-all"
      }`}
    >
      <Link to={`/blog/${post.slug}`} className="flex flex-col h-full">
        <div className={`relative overflow-hidden ${featured ? "h-64 lg:h-96" : "h-48"}`}>
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-4 left-4 flex gap-2">
            <span className="bg-secondary text-on-secondary font-data-mono text-data-mono px-2 py-1 rounded text-xs uppercase tracking-wider">
              {post.category}
            </span>
          </div>
        </div>
        <div className={`flex-grow flex flex-col justify-between ${featured ? "p-card-padding" : "p-stack-lg"}`}>
          <div>
            <div className="flex items-center gap-2 mb-stack-sm text-on-surface-variant dark:text-surface-variant font-data-mono text-data-mono">
              <span className="material-symbols-outlined text-[16px]">calendar_today</span>
              <span>{post.displayDate}</span>
              <span>·</span>
              <span>{post.readTime}</span>
            </div>
            {featured ? (
              <h2 className="font-headline-section text-headline-section text-on-surface dark:text-surface-bright mb-stack-md group-hover:text-secondary transition-colors">
                {post.title}
              </h2>
            ) : (
              <h3 className="font-headline-card text-headline-card text-on-surface dark:text-surface-bright mb-stack-md group-hover:text-primary transition-colors">
                {post.title}
              </h3>
            )}
            <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant mb-stack-lg line-clamp-3">
              {post.excerpt}
            </p>
          </div>
          <span className="inline-flex items-center gap-2 font-label-sm text-label-sm text-primary uppercase tracking-wider group-hover:underline mt-auto">
            Read analysis <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
          </span>
        </div>
      </Link>
    </motion.article>
  );
}

export default function Blog() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Cybermon Blog",
    description: "Breach claims, underground forum activity, and digital risk analysis from the CyberMon research team.",
    url: absoluteUrl("/blog"),
    blogPost: BLOG_POSTS.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      url: absoluteUrl(`/blog/${post.slug}`),
    })),
  };

  return (
    <main className="flex-grow pt-16 md:pt-[120px] pb-section-gap px-margin-mobile md:px-gutter max-w-container-max mx-auto w-full">
      <Seo
        title="Blog"
        description="Breach claims, underground forum activity, and digital risk analysis from the CyberMon research team — written for security leaders who need signal, not noise."
        path="/blog"
        jsonLd={jsonLd}
      />

      <header className="mb-section-gap max-w-3xl">
        <p className="font-eyebrow text-eyebrow text-secondary mb-stack-md uppercase tracking-widest">
          CYBERMON DRI ANALYSIS
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-display-hero-mobile md:font-display-hero text-display-hero-mobile md:text-display-hero text-on-surface dark:text-surface-bright mb-stack-lg"
        >
          Blog
        </motion.h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant dark:text-surface-variant">
          Breach claims, underground forum activity, and digital risk analysis from the CyberMon
          research team — written for security leaders who need signal, not noise.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-stack-lg">
        {BLOG_POSTS.map((post, i) => (
          <BlogCard key={post.slug} post={post} featured={i === 0} index={i} />
        ))}
      </div>
    </main>
  );
}
